import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  FormField,
  Icon,
  MoneyInput,
  NumberStepper,
  SegmentedControl,
  Select,
  Text,
  Textarea,
  todayIso,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { formErrors } from '../../../lib/errors';
import { useCustomers } from '../../crm/api';
import { useSession } from '../../identity/session';
import {
  useCreateSale,
  usePrices,
  useUpdateSale,
  type PaymentMethod,
  type Sale,
  type SaleHandover,
  type SaleTarget,
} from '../api';
import { formatEuro } from '../format';
import { ReferrerField } from './Referrer';
import styles from './sales.module.css';

interface Line {
  key: number;
  article: string;
  quantity: number | null;
  price: number | null;
  discount: number | null;
}

export interface SaleFormDialogProps {
  open: boolean;
  onClose: () => void;
  sale?: Sale;
  customerId?: string;
  onSaved?: (sale: Sale) => void;
}

const METHODS: PaymentMethod[] = ['cash', 'transfer', 'paypal', 'other'];
const HANDOVERS: SaleHandover[] = ['pickup', 'delivery', 'shipping'];

let nextKey = 1;

/** Records or edits a sale (SAL-001) – also in one step (SAL-006). */
export function SaleFormDialog({ open, onClose, sale, customerId, onSaved }: SaleFormDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { me } = useSession();
  const customers = useCustomers({}, open);
  const prices = usePrices(open);
  const create = useCreateSale();
  const update = useUpdateSale();
  const mutation = sale ? update : create;
  const [customer, setCustomer] = useState(sale?.customer?.id ?? customerId ?? '');
  const [soldOn, setSoldOn] = useState(sale?.sold_on ?? todayIso());
  const [lines, setLines] = useState<Line[]>(
    sale?.lines.map((l) => ({
      key: nextKey++,
      article: l.article.id,
      quantity: l.quantity,
      price: l.unit_price_cents,
      discount: l.discount_cents || null,
    })) ?? [{ key: nextKey++, article: '', quantity: 1, price: null, discount: null }],
  );
  const [discount, setDiscount] = useState<number | null>(sale?.discount_cents || null);
  const [handover, setHandover] = useState<string>(sale?.handover ?? '');
  const [notes, setNotes] = useState(sale?.notes ?? '');
  // "Vermittelt von" (#26): the signed-in person unless chosen otherwise.
  const [referredBy, setReferredBy] = useState(sale?.referred_by.id ?? me.id);
  const [target, setTarget] = useState<SaleTarget>('delivered');
  const [paid, setPaid] = useState(true);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const articles = (prices.data ?? []).filter(
    (a) => !a.archived || lines.some((l) => l.article === a.article.id),
  );
  const info = (id: string) => prices.data?.find((p) => p.article.id === id);
  const unit = (l: Line) => l.price ?? info(l.article)?.current_cents ?? 0;
  const lineTotal = (l: Line) => Math.max(0, unit(l) * (l.quantity ?? 0) - (l.discount ?? 0));
  const subtotal = lines.reduce((s, l) => s + lineTotal(l), 0);
  const total = Math.max(0, subtotal - (discount ?? 0));
  const errors = formErrors(mutation.error, {
    customer: ['sale.customer', 'customer.'],
    lines: ['sale.lines', 'sale.quantity', 'sale.price', 'article.'],
    discount: ['sale.discount'],
    referredBy: ['sale.referrer'],
  });
  const close = () => {
    mutation.reset();
    onClose();
  };
  const setLine = (key: number, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const submit = () => {
    const body = {
      customer_id: customer || null,
      sold_on: soldOn,
      discount_cents: discount ?? 0,
      handover: (handover || null) as SaleHandover | null,
      notes: notes || null,
      referred_by_id: referredBy,
      lines: lines
        .filter((l) => l.article)
        .map((l) => ({
          article_id: l.article,
          quantity: l.quantity ?? 0,
          unit_price_cents: l.price,
          discount_cents: l.discount ?? 0,
          batch_id: null,
        })),
    };
    const done = (s: Sale) => {
      toast.show(sale ? t('sales.form.saved') : t(`sales.form.recorded.${target}`));
      onSaved?.(s);
      close();
    };
    if (sale) {
      update.mutate({ id: sale.id, body, version: sale.version }, { onSuccess: done });
    } else {
      create.mutate(
        {
          ...body,
          target,
          payment: target !== 'draft' && paid ? { method, paid_on: soldOn } : null,
        },
        { onSuccess: done },
      );
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      drawer
      size="lg"
      title={sale ? t('sales.form.editTitle') : t('sales.form.newTitle')}
      submitLabel={sale ? t('common.save') : t(`sales.form.submit.${target}`)}
      onSubmit={submit}
      pending={mutation.isPending}
      error={errors.general}
    >
      <div className={styles.row}>
        <FormField label={t('sales.form.customer')} required error={errors.field.customer}>
          {(p) => (
            <Select
              {...p}
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              options={[
                { value: '', label: t('sales.form.chooseCustomer') },
                ...(customers.data ?? []).map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
          )}
        </FormField>
        <FormField label={t('sales.form.date')} required>
          {(p) => <DatePicker {...p} value={soldOn} onChange={setSoldOn} />}
        </FormField>
      </div>

      <fieldset className={styles.lines} aria-invalid={errors.field.lines ? true : undefined}>
        <legend>{t('sales.form.lines')}</legend>
        {lines.map((l, i) => {
          const a = info(l.article);
          const available = a ? a.jars - a.reserved : undefined;
          return (
            <div key={l.key} className={styles.line}>
              <FormField label={t('sales.form.article', { n: i + 1 })}>
                {(p) => (
                  <Select
                    {...p}
                    value={l.article}
                    onChange={(e) => setLine(l.key, { article: e.target.value, price: null })}
                    options={[
                      { value: '', label: t('sales.form.chooseArticle') },
                      ...articles.map((x) => ({
                        value: x.article.id,
                        label:
                          x.current_cents != null
                            ? `${x.article.name} · ${formatEuro(x.current_cents)}`
                            : x.article.name,
                      })),
                    ]}
                  />
                )}
              </FormField>
              <FormField label={t('sales.form.quantity')}>
                {(p) => (
                  <NumberStepper
                    {...p}
                    value={l.quantity}
                    onChange={(q) => setLine(l.key, { quantity: q })}
                    min={1}
                    labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
                  />
                )}
              </FormField>
              <FormField label={t('sales.form.price')}>
                {(p) => (
                  <MoneyInput
                    {...p}
                    value={l.price}
                    placeholder={
                      a?.current_cents != null
                        ? (a.current_cents / 100).toFixed(2).replace('.', ',')
                        : ''
                    }
                    onChange={(c) => setLine(l.key, { price: c })}
                    labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
                  />
                )}
              </FormField>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('sales.form.removeLine', { n: i + 1 })}
                iconStart={<Icon name="trash" size="sm" />}
                disabled={lines.length === 1}
                onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
              />
              {a && (
                <Text
                  size="xs"
                  tone={
                    available !== undefined && (l.quantity ?? 0) > available ? 'danger' : 'muted'
                  }
                  className={styles.lineHint}
                >
                  {t('sales.form.stock', { jars: a.jars, available: Math.max(0, available ?? 0) })}
                  {a.current_cents == null && l.price == null && ` · ${t('sales.form.noPrice')}`}
                  {' · '}
                  {formatEuro(lineTotal(l))}
                </Text>
              )}
            </div>
          );
        })}
        {errors.field.lines && (
          <Text size="xs" tone="danger" role="alert">
            {errors.field.lines}
          </Text>
        )}
        <Button
          variant="secondary"
          size="sm"
          iconStart={<Icon name="plus" size="sm" />}
          onClick={() =>
            setLines((ls) => [
              ...ls,
              { key: nextKey++, article: '', quantity: 1, price: null, discount: null },
            ])
          }
        >
          {t('sales.form.addLine')}
        </Button>
      </fieldset>

      <div className={styles.row}>
        <FormField label={t('sales.form.discount')} error={errors.field.discount}>
          {(p) => (
            <MoneyInput
              {...p}
              value={discount}
              onChange={setDiscount}
              labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
            />
          )}
        </FormField>
        <FormField label={t('sales.form.handover')}>
          {(p) => (
            <Select
              {...p}
              value={handover}
              onChange={(e) => setHandover(e.target.value)}
              options={[
                { value: '', label: t('sales.handover.none') },
                ...HANDOVERS.map((h) => ({ value: h, label: t(`sales.handover.${h}`) })),
              ]}
            />
          )}
        </FormField>
      </div>
      <ReferrerField
        value={referredBy}
        onChange={setReferredBy}
        current={sale?.referred_by}
        error={errors.field.referredBy}
        enabled={open}
      />
      <dl className={styles.totals} aria-live="polite">
        {(discount ?? 0) > 0 && (
          <>
            <dt>{t('sales.form.subtotal')}</dt>
            <dd>{formatEuro(subtotal)}</dd>
            <dt>{t('sales.form.discount')}</dt>
            <dd>−{formatEuro(discount ?? 0)}</dd>
          </>
        )}
        <dt className={styles.grand}>{t('sales.form.total')}</dt>
        <dd className={styles.grand}>{formatEuro(total)}</dd>
      </dl>

      {!sale && (
        <>
          <SegmentedControl
            label={t('sales.form.target')}
            value={target}
            onChange={(v) => {
              setTarget(v);
              // Orders are usually paid later, quick sales right away.
              setPaid(v === 'delivered');
            }}
            fullWidth
            options={[
              { value: 'draft', label: t('sales.target.draft') },
              { value: 'ordered', label: t('sales.target.ordered') },
              { value: 'delivered', label: t('sales.target.delivered') },
            ]}
          />
          <Text size="xs" tone="muted">
            {t(`sales.targetHint.${target}`)}
          </Text>
          {target !== 'draft' && (
            <div className={styles.row}>
              <Checkbox
                label={t('sales.form.paidNow')}
                checked={paid}
                onChange={(e) => setPaid(e.target.checked)}
              />
              {paid && (
                <FormField label={t('sales.form.method')}>
                  {(p) => (
                    <Select
                      {...p}
                      value={method}
                      onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                      options={METHODS.map((m) => ({ value: m, label: t(`sales.method.${m}`) }))}
                    />
                  )}
                </FormField>
              )}
            </div>
          )}
        </>
      )}
      {sale && sale.warnings.length > 0 && (
        <Alert tone="warning">{t('sales.warnings.title')}</Alert>
      )}
      <FormField label={t('sales.form.notes')}>
        {(p) => (
          <Textarea {...p} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        )}
      </FormField>
    </FormDialog>
  );
}
